import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programmesApi } from '../../api/programmes';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

const formSchema = z.object({ name: z.string().min(3, 'Name must be at least 3 characters.') });
type FormValues = z.infer<typeof formSchema>;

export function ProgrammesPage() {
  const canManage = usePermission('programmes.manage');
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(searchParams.get('new') === '1');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.get('new') === '1') setModalOpen(true);
  }, [searchParams]);

  const programmesQuery = useQuery({ queryKey: ['programmes'], queryFn: () => programmesApi.list() });
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => programmesApi.create(values),
    onSuccess: () => {
      showSuccess('Programme created.');
      queryClient.invalidateQueries({ queryKey: ['programmes'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create programme.'),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Programmes</h1>
        {canManage && <button onClick={() => setModalOpen(true)}>New Programme</button>}
      </div>

      {programmesQuery.isLoading && <p>Loading…</p>}
      {programmesQuery.isError && <p className="error-text">Could not load programmes.</p>}

      {programmesQuery.data && (
        <DataTable
          rows={programmesQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="No programmes yet."
          columns={[
            { header: 'Reference', render: (p) => p.programme_reference ?? '-' },
            { header: 'Name', render: (p) => <Link to={`/programmes/${p.id}`}>{p.name}</Link> },
            { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Programme" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Create'}</button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
